// The activity catalog: public MET data (2011 Adult Compendium of Physical
// Activities, values rounded to one decimal), an emoji for EVERY entry, and
// EN+DE search synonyms so "fix car" or "putzen" land on the right activity.
//
// Display names live in the locale files as `act.<id>`; this module stays
// language-agnostic and takes a name resolver for search. Calories follow the
// standard MET formula: MET × weight(kg) × hours. Each entry maps onto one of
// the legacy ActivityType values so Health sync, stats and old rows keep
// working untouched.
import type { ActivityType } from "./types";

export type ActivityCategory =
  | "walk" | "cardio" | "cycle" | "water" | "strength" | "sport"
  | "outdoor" | "dance" | "mind" | "home" | "repair" | "garden";

export interface CatalogActivity {
  id: string;
  met: number;
  emoji: string;
  category: ActivityCategory;
  /** Lowercase search words beyond the localized name (EN + DE). */
  syn?: string[];
}

const CATEGORY_META: Record<ActivityCategory, { emoji: string; legacy: ActivityType }> = {
  walk:     { emoji: "🚶", legacy: "walk" },
  cardio:   { emoji: "🏃", legacy: "run" },
  cycle:    { emoji: "🚴", legacy: "cycle" },
  water:    { emoji: "🏊", legacy: "swim" },
  strength: { emoji: "🏋️", legacy: "strength" },
  sport:    { emoji: "⚽", legacy: "other" },
  outdoor:  { emoji: "🥾", legacy: "hike" },
  dance:    { emoji: "💃", legacy: "other" },
  mind:     { emoji: "🧘", legacy: "other" },
  home:     { emoji: "🧹", legacy: "other" },
  repair:   { emoji: "🛠️", legacy: "other" },
  garden:   { emoji: "🌱", legacy: "other" },
};

export const ACTIVITY_CATALOG: CatalogActivity[] = [
  // ── walking ──
  { id: "walk_general", met: 3.5, emoji: "🚶", category: "walk", syn: ["spazieren", "gehen", "stroll", "walking"] },
  { id: "walk_brisk", met: 4.3, emoji: "🚶‍♀️", category: "walk", syn: ["power walk", "schnell gehen", "brisk"] },
  { id: "walk_dog", met: 3.0, emoji: "🐕", category: "walk", syn: ["dog", "hund", "gassi"] },
  { id: "stair_climbing", met: 8.0, emoji: "🪜", category: "walk", syn: ["treppen", "treppensteigen", "stairs"] },
  // ── cardio ──
  { id: "jog", met: 7.0, emoji: "🏃", category: "cardio", syn: ["joggen", "jogging", "trab"] },
  { id: "run_general", met: 8.0, emoji: "🏃‍♂️", category: "cardio", syn: ["laufen", "rennen", "running"] },
  { id: "run_fast", met: 11.5, emoji: "🏃‍♀️", category: "cardio", syn: ["tempolauf", "sprint", "race"] },
  { id: "hiit", met: 8.0, emoji: "🔥", category: "cardio", syn: ["intervall", "tabata", "interval"] },
  { id: "jump_rope", met: 11.0, emoji: "🪢", category: "cardio", syn: ["seilspringen", "skipping", "rope"] },
  { id: "rowing_machine", met: 7.0, emoji: "🚣", category: "cardio", syn: ["rudern", "ergometer", "rowing"] },
  { id: "elliptical", met: 5.0, emoji: "🌀", category: "cardio", syn: ["crosstrainer", "cross trainer"] },
  { id: "stairmaster", met: 9.0, emoji: "🧗‍♀️", category: "cardio", syn: ["stepper", "stair machine"] },
  // ── cycling ──
  { id: "cycle_easy", met: 4.0, emoji: "🚲", category: "cycle", syn: ["gemütlich radeln", "leisure bike"] },
  { id: "cycle_general", met: 7.5, emoji: "🚴‍♂️", category: "cycle", syn: ["radfahren", "fahrrad", "bike", "biking"] },
  { id: "cycle_vigorous", met: 10.0, emoji: "🚴‍♀️", category: "cycle", syn: ["rennrad", "race bike", "schnell"] },
  { id: "mtb", met: 8.5, emoji: "🚵", category: "cycle", syn: ["mountainbike", "mountain bike", "trail"] },
  { id: "ebike", met: 4.0, emoji: "🚲", category: "cycle", syn: ["e-bike", "pedelec", "ebike"] },
  { id: "spinning", met: 8.5, emoji: "🚴", category: "cycle", syn: ["indoor cycling", "spinning"] },
  // ── water ──
  { id: "swim_general", met: 6.0, emoji: "🏊", category: "water", syn: ["schwimmen", "swimming", "baden"] },
  { id: "swim_vigorous", met: 9.8, emoji: "🏊‍♂️", category: "water", syn: ["kraulen", "bahnen", "laps", "freestyle"] },
  { id: "aqua_fitness", met: 5.3, emoji: "💦", category: "water", syn: ["wassergymnastik", "aqua aerobic"] },
  { id: "kayak", met: 5.0, emoji: "🛶", category: "water", syn: ["paddeln", "kanu", "canoe"] },
  { id: "surfing", met: 3.5, emoji: "🏄", category: "water", syn: ["surfen", "wellenreiten"] },
  // ── strength ──
  { id: "strength_general", met: 5.0, emoji: "🏋️‍♂️", category: "strength", syn: ["gym", "krafttraining", "weights", "lifting", "fitnessstudio", "hanteln"] },
  { id: "calisthenics", met: 3.8, emoji: "🤸", category: "strength", syn: ["bodyweight", "eigengewicht", "liegestütze", "pushups"] },
  { id: "calisthenics_vigorous", met: 8.0, emoji: "🤸‍♂️", category: "strength", syn: ["burpees", "vigorous"] },
  { id: "circuit_training", met: 8.0, emoji: "🏋️‍♀️", category: "strength", syn: ["zirkeltraining", "crossfit", "functional", "circuit"] },
  { id: "climbing", met: 7.5, emoji: "🧗", category: "strength", syn: ["klettern", "bouldern", "boulder", "rock climbing"] },
  // ── sport ──
  { id: "soccer_general", met: 7.0, emoji: "⚽️", category: "sport", syn: ["fußball", "fussball", "football", "kicken", "soccer"] },
  { id: "basketball_general", met: 6.5, emoji: "🏀", category: "sport", syn: ["basketball", "korbball"] },
  { id: "tennis_general", met: 7.3, emoji: "🎾", category: "sport", syn: ["tennis"] },
  { id: "badminton", met: 5.5, emoji: "🏸", category: "sport", syn: ["federball"] },
  { id: "table_tennis", met: 4.0, emoji: "🏓", category: "sport", syn: ["tischtennis", "ping pong"] },
  { id: "volleyball", met: 4.0, emoji: "🏐", category: "sport", syn: ["volleyball", "beachvolleyball"] },
  { id: "golf", met: 4.8, emoji: "⛳", category: "sport", syn: ["golfen"] },
  { id: "bowling", met: 3.8, emoji: "🎳", category: "sport", syn: ["kegeln"] },
  { id: "martial_arts", met: 10.3, emoji: "🥋", category: "sport", syn: ["kampfsport", "karate", "judo", "mma", "taekwondo"] },
  { id: "boxing_bag", met: 5.5, emoji: "🥊", category: "sport", syn: ["boxen", "sandsack", "boxing"] },
  { id: "skateboard", met: 5.0, emoji: "🛹", category: "sport", syn: ["skaten", "skateboarding"] },
  { id: "horse_riding", met: 5.5, emoji: "🏇", category: "sport", syn: ["reiten", "pferd"] },
  { id: "skiing", met: 5.3, emoji: "⛷️", category: "sport", syn: ["ski", "skifahren", "alpin"] },
  { id: "skiing_xc", met: 9.0, emoji: "🎿", category: "sport", syn: ["langlauf", "cross country"] },
  { id: "ice_skating", met: 7.0, emoji: "⛸️", category: "sport", syn: ["schlittschuh", "eislaufen"] },
  { id: "frisbee", met: 3.0, emoji: "🥏", category: "sport", syn: ["frisbee"] },
  { id: "fishing", met: 3.5, emoji: "🎣", category: "sport", syn: ["angeln"] },
  // ── outdoor ──
  { id: "hike_general", met: 6.0, emoji: "🥾", category: "outdoor", syn: ["wandern", "bergwandern", "hiking", "wanderung"] },
  { id: "backpacking", met: 7.8, emoji: "🎒", category: "outdoor", syn: ["trekking", "hüttentour"] },
  // ── dance ──
  { id: "dance_general", met: 5.0, emoji: "💃", category: "dance", syn: ["tanzen", "dancing", "disco"] },
  { id: "zumba", met: 7.0, emoji: "🕺", category: "dance", syn: ["zumba", "aerobic"] },
  { id: "ballet", met: 5.0, emoji: "🩰", category: "dance", syn: ["ballett"] },
  // ── mind/body ──
  { id: "yoga_general", met: 2.5, emoji: "🧘", category: "mind", syn: ["yoga", "hatha"] },
  { id: "yoga_power", met: 4.0, emoji: "🧘‍♂️", category: "mind", syn: ["vinyasa", "power yoga"] },
  { id: "pilates", met: 3.0, emoji: "🧘‍♀️", category: "mind", syn: ["pilates"] },
  { id: "stretching", met: 2.3, emoji: "🙆", category: "mind", syn: ["dehnen", "mobility", "stretch"] },
  // ── household ──
  { id: "cleaning_general", met: 3.3, emoji: "🧹", category: "home", syn: ["putzen", "saubermachen", "hausputz", "aufräumen", "chores", "tidy", "haushalt"] },
  { id: "cleaning_vigorous", met: 3.8, emoji: "🧽", category: "home", syn: ["frühjahrsputz", "schrubben", "scrub"] },
  { id: "laundry", met: 2.0, emoji: "🧺", category: "home", syn: ["wäsche", "bügeln"] },
  { id: "cooking", met: 2.0, emoji: "🍳", category: "home", syn: ["kochen", "backen"] },
  { id: "grocery_shopping", met: 2.3, emoji: "🛒", category: "home", syn: ["einkaufen", "shopping"] },
  { id: "moving_furniture", met: 5.8, emoji: "📦", category: "home", syn: ["umzug", "möbel", "tragen", "moving"] },
  { id: "window_cleaning", met: 3.2, emoji: "🪟", category: "home", syn: ["fenster putzen", "windows"] },
  { id: "playing_kids", met: 4.0, emoji: "👶", category: "home", syn: ["kinder", "spielen", "toben"] },
  // ── repairs ──
  { id: "home_repair", met: 3.0, emoji: "🛠️", category: "repair", syn: ["reparieren", "heimwerken", "diy", "renovieren", "repair"] },
  { id: "painting_walls", met: 3.3, emoji: "🖌️", category: "repair", syn: ["streichen", "malern", "tapezieren"] },
  { id: "carpentry", met: 3.6, emoji: "🪚", category: "repair", syn: ["holzarbeit", "sägen", "schreinern"] },
  { id: "car_repair", met: 3.3, emoji: "🚗", category: "repair", syn: ["fix car", "auto reparieren", "auto", "kfz", "werkstatt", "schrauben"] },
  // ── garden ──
  { id: "gardening_general", met: 3.8, emoji: "🌱", category: "garden", syn: ["gartenarbeit", "garten", "pflanzen", "gardening"] },
  { id: "mowing", met: 5.5, emoji: "🌿", category: "garden", syn: ["rasen mähen", "rasenmähen", "mähen", "lawn"] },
  { id: "digging", met: 5.0, emoji: "⛏️", category: "garden", syn: ["umgraben", "schaufeln", "beet"] },
  { id: "raking", met: 3.8, emoji: "🍂", category: "garden", syn: ["laub", "rechen", "harken"] },
  { id: "snow_shoveling", met: 5.3, emoji: "❄️", category: "garden", syn: ["schnee schippen", "schneeschaufeln", "schnee"] },
  { id: "wood_chopping", met: 6.3, emoji: "🪓", category: "garden", syn: ["holz hacken", "holzhacken", "brennholz"] },
];

/** The quick-select grid, in the owner's order. */
export const POPULAR_ACTIVITIES: string[] = [
  "walk_general", "cycle_general", "run_general", "strength_general",
  "hike_general", "yoga_general", "swim_general", "dance_general",
  "soccer_general", "basketball_general", "tennis_general",
  "cleaning_general", "home_repair", "car_repair", "gardening_general",
];

const byId = new Map(ACTIVITY_CATALOG.map((a) => [a.id, a]));

export function activityById(id: string): CatalogActivity | null {
  return byId.get(id) ?? null;
}

/** Emoji is required everywhere: entry's own, else its category's, else 🔥. */
export function emojiOf(a: CatalogActivity): string {
  return a.emoji || CATEGORY_META[a.category].emoji || "🔥";
}

export function legacyTypeOf(a: CatalogActivity): ActivityType {
  return CATEGORY_META[a.category].legacy;
}

/** MET × weight(kg) × hours, rounded to the nearest kcal. */
export function activityKcal(met: number, weightKg: number, minutes: number): number {
  return Math.round(met * weightKg * (minutes / 60));
}

/** Search by localized name (resolver-supplied), id words, and synonyms —
 *  prefix beats substring beats synonym, capped at 20 rows. */
export function searchActivities(
  query: string,
  nameOf: (id: string) => string,
): CatalogActivity[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const scored: Array<{ a: CatalogActivity; score: number }> = [];
  for (const a of ACTIVITY_CATALOG) {
    const name = nameOf(a.id).toLowerCase();
    let score = 0;
    if (name.startsWith(q)) score = 3;
    else if (name.includes(q)) score = 2;
    else if ((a.syn ?? []).some((s) => s.includes(q) || q.includes(s))) score = 1;
    else if (a.id.replace(/_/g, " ").includes(q)) score = 1;
    if (score > 0) scored.push({ a, score });
  }
  return scored.sort((x, y) => y.score - x.score).map((x) => x.a).slice(0, 20);
}
